// Event tracker service (separate from pvx-core), e.g. https://api-event-tracker-alpha.pvxpartners.com
export const EVENT_TRACKER_URL = process.env.REACT_APP_EVENT_TRACKER_URL || '';

export const USER_EVENTS_PATH = '/api/user-events';

export const ACTIVITY_LOGGER_ENABLED =
  process.env.REACT_APP_ACTIVITY_LOGGER_ENABLED === 'true' && Boolean(EVENT_TRACKER_URL);

// Logs each batch and its response status in the browser console
export const ACTIVITY_LOGGER_DEBUG = process.env.REACT_APP_ACTIVITY_LOGGER_DEBUG === 'true';

// Inactivity longer than this ends the view (5 min, pending stakeholder confirmation)
export const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

// How often queued events are sent to the API
export const FLUSH_INTERVAL_MS = 30 * 1000;

// Backoff after a temporary server error (503, 5xx, network): 5s, 15s, then every 60s
export const RETRY_DELAYS_MS = [5 * 1000, 15 * 1000, 60 * 1000];

// A batch that keeps failing with 500 is dropped after this many retries
export const MAX_SERVER_ERROR_RETRIES = 3;

// How long a manual logout waits for its events to be sent before the pvx-core session ends
export const LOGOUT_FLUSH_TIMEOUT_MS = 2 * 1000;

// How often the tracker checks focus / input / sleep
export const TICK_INTERVAL_MS = 1000;

// How often the open view's last activity time is saved for crash recovery
export const SAVE_INTERVAL_MS = 5 * 1000;

// A gap between ticks larger than this, while the page is visible, means the device slept
export const SLEEP_GAP_MS = 10 * 1000;

// An open view not saved for this long belongs to a closed or crashed tab
export const STALE_VIEW_MS = IDLE_TIMEOUT_MS + 60 * 1000;

// The server rejects events that occurred more than 7 days ago; the hour of margin covers
// differences between the browser and server clocks
export const MAX_EVENT_AGE_MS = 7 * 24 * 60 * 60 * 1000 - 60 * 60 * 1000;

// API limit per request
export const MAX_EVENTS_PER_BATCH = 100;

// The browser allows 64 KB for all keepalive requests in flight together. A tab that closes
// sends one when it is hidden and another as it closes, so each is kept under half of that.
export const MAX_KEEPALIVE_BATCH_BYTES = 30 * 1024;

// API limit for resourceId and sessionId
export const MAX_ID_LENGTH = 128;

export const EVENT_TYPES = {
  // Viewing a dashboard (submodule)
  DASHBOARD_VIEW: 'DASHBOARD_VIEW',
  // Viewing any other page, e.g. user management or deal setup
  PAGE_VIEW: 'PAGE_VIEW',
  USER_SESSION: 'USER_SESSION',
  USER_ACCOUNT: 'USER_ACCOUNT',
  ROAS_PROJECTIONS: 'ROAS_PROJECTIONS',
  // Investor report V2 (company reports)
  INVESTOR_REPORT: 'INVESTOR_REPORT',
} as const;

export const EVENT_NAMES = {
  VIEW_STARTED: 'VIEW_STARTED',
  VIEW_IDLE: 'VIEW_IDLE',
  VIEW_ENDED: 'VIEW_ENDED',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  SIGN_UP: 'SIGN_UP',
  PROJECTIONS_REQUESTED: 'PROJECTIONS_REQUESTED',
  SCENARIO_CREATED: 'SCENARIO_CREATED',
  GENERATED: 'GENERATED',
  REFRESHED: 'REFRESHED',
  GAME_LIST_CHANGED: 'GAME_LIST_CHANGED',
  EDITS_SUGGESTED: 'EDITS_SUGGESTED',
  SHARED: 'SHARED',
} as const;

// UTM parameters read from the sign-up page URL
export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export const RESOURCE_TYPES = {
  // A dashboard (submodule): resourceId is the submodule id
  DASHBOARD: 'DASHBOARD',
  // Any other page, e.g. user management: resourceId is the route
  PAGE: 'PAGE',
  // A game: resourceId is the game id
  GAME: 'GAME',
  // A company (investor report V2): resourceId is the client code
  COMPANY: 'COMPANY',
} as const;

export const STORAGE_KEYS = {
  QUEUE: 'activityLogger:queue',
  OPEN_VIEW_PREFIX: 'activityLogger:openView:',
  PENDING_LOGIN: 'activityLogger:pendingLogin',
  // Sign-ups recorded before the user had a token, sent at their first login
  PENDING_SIGN_UPS: 'activityLogger:pendingSignUps',
};

export const FLUSH_LOCK_NAME = 'activityLogger:flush';
